import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  setWorkspaces,
  setCurrentWorkspace,
  setCurrentPath,
} from '../store/workspaceSlice';
import * as api from '../api';
import { Workspace } from '../types';

export function useWorkspaces() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { workspaces, currentWorkspace, currentPath, loading, error } =
    useSelector((state: RootState) => state.workspace);

  const workspacesQuery = useQuery({
    queryKey: ['workspaces'],
    queryFn: api.getWorkspaces,
  });

  const workspaceQuery = (name: string) =>
    useQuery({
      queryKey: ['workspace', name],
      queryFn: () => api.getWorkspace(name),
      enabled: !!name,
    });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Workspace>) => api.createWorkspace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ name, data }: { name: string; data: Partial<Workspace> }) =>
      api.updateWorkspace(name, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => api.deleteWorkspace(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  return {
    workspaces: workspacesQuery.data || workspaces,
    currentWorkspace,
    currentPath,
    loading: workspacesQuery.isLoading || loading,
    error: workspacesQuery.error?.message || error,
    setCurrentWorkspace: (ws: Workspace | null) =>
      dispatch(setCurrentWorkspace(ws)),
    setCurrentPath: (path: string) => dispatch(setCurrentPath(path)),
    refreshWorkspaces: () =>
      queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
    createWorkspace: createMutation.mutate,
    updateWorkspace: updateMutation.mutate,
    deleteWorkspace: deleteMutation.mutate,
    workspaceQuery,
  };
}
